// components/tabs/GeneralTab.jsx
import React from 'react';
import { Save, Building2, MapPin, Globe, Upload } from 'lucide-react';

export default function GeneralTab({ 
    schoolConfig, 
    logoPreview, 
    handleInputChange, 
    setLogoPreview, 
    handleSave, 
    isSaving 
}) {
    return (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-8 shadow-xs font-dynamic-title">
            
            {/* Main Section Header */}
            <div className="border-b border-zinc-100 pb-4">
                <h2 className="text-sm font-bold text-zinc-900 tracking-tight">Institution Profile</h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                    Primary identity markers, regional localization, and operational metadata entities.
                </p>
            </div>

            {/* SUB-SECTION 1: Core Branding & Identity */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-zinc-500" />
                    <h3 className="text-[11px] font-bold text-zinc-800 uppercase tracking-wider">
                        1. Core Branding & Identity
                    </h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start bg-zinc-50/50 p-4 rounded-lg border border-zinc-200/60">
                    {/* School Logo Upload Field */}
                    <div className="md:col-span-1 flex flex-col items-center justify-center space-y-2">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider text-center">
                            Institution Seal
                        </label>
                        <div className="relative group w-28 h-28 bg-white border border-dashed border-zinc-300 rounded-xl flex flex-col items-center justify-center overflow-hidden transition-all hover:border-blue-500 shadow-xs">
                            {logoPreview ? (
                                <img src={logoPreview} alt="School Preview" className="w-full h-full object-cover" />
                            ) : (
                                <div className="flex flex-col items-center justify-center p-2 text-center space-y-1">
                                    <Upload className="w-4 h-4 text-zinc-400 group-hover:text-blue-600 transition-colors" />
                                    <span className="text-zinc-500 group-hover:text-blue-600 transition-colors text-[11px] font-medium">
                                        Upload Logo
                                    </span>
                                </div>
                            )}
                            <input 
                                type="file" 
                                name="school_logo"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setLogoPreview(URL.createObjectURL(file));
                                    }
                                }}
                                className="absolute inset-0 opacity-0 cursor-pointer" 
                            />
                        </div>
                        <span className="text-[9px] text-zinc-400 text-center font-medium">PNG or JPG up to 2MB</span>
                    </div>

                    {/* Name, Code & Principal */}
                    <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">
                                School Workspace Identity Name <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="name"
                                value={schoolConfig.name || ''}
                                onChange={handleInputChange}
                                placeholder="e.g. Horizon International Academy"
                                className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">
                                Unique School Code <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                name="school_code"
                                value={schoolConfig.school_code || ''}
                                onChange={handleInputChange}
                                placeholder="e.g. HZN-MAIN"
                                className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs"
                            />
                        </div>

                        <div className="space-y-1.5 sm:col-span-2">
                            <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">
                                Principal / Managing Director Name
                            </label>
                            <input 
                                type="text" 
                                name="principal_name"
                                value={schoolConfig.principal_name || ''}
                                onChange={handleInputChange}
                                placeholder="Dr. Eleanor Vance"
                                className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* SUB-SECTION 2: Regional Placement & Physical Address */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-zinc-500" />
                    <h3 className="text-[11px] font-bold text-zinc-800 uppercase tracking-wider">
                        2. Localization & Infrastructure Placement
                    </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-zinc-50/50 p-4 rounded-lg border border-zinc-200/60">
                    <div className="space-y-1.5 md:col-span-3">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">
                            Street Address Description
                        </label>
                        <textarea 
                            name="address"
                            rows="2"
                            value={schoolConfig.address || ''}
                            onChange={handleInputChange}
                            placeholder="100 Educational Parkway, Suite B"
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden resize-none shadow-2xs"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">City</label>
                        <input 
                            type="text" 
                            name="city" 
                            value={schoolConfig.city || ''} 
                            onChange={handleInputChange} 
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs" 
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">State / Province</label>
                        <input 
                            type="text" 
                            name="state" 
                            value={schoolConfig.state || ''} 
                            onChange={handleInputChange} 
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs" 
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Postal / ZIP Code</label>
                        <input 
                            type="text" 
                            name="postal_code" 
                            value={schoolConfig.postal_code || ''} 
                            onChange={handleInputChange} 
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs" 
                        />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Country</label>
                        <input 
                            type="text" 
                            name="country" 
                            value={schoolConfig.country || ''} 
                            onChange={handleInputChange} 
                            placeholder="United States" 
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs" 
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">System Localization Timezone</label>
                        <select 
                            name="timezone" 
                            value={schoolConfig.timezone || 'UTC'} 
                            onChange={handleInputChange} 
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden cursor-pointer shadow-2xs"
                        >
                            <option value="UTC">Coordinated Universal Time (UTC)</option>
                            <option value="GMT +7">Indochina Time (GMT+7)</option>
                            <option value="GMT +8">Singapore Standard Time (GMT+8)</option>
                            <option value="EST">Eastern Standard Time (EST)</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* SUB-SECTION 3: System Contact Matrix */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5 text-zinc-500" />
                    <h3 className="text-[11px] font-bold text-zinc-800 uppercase tracking-wider">
                        3. Contact Channels & Networks
                    </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-zinc-50/50 p-4 rounded-lg border border-zinc-200/60">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Primary System Phone</label>
                        <input 
                            type="text" 
                            name="phone" 
                            value={schoolConfig.phone || ''} 
                            onChange={handleInputChange} 
                            placeholder="+1 (555) 000-0000" 
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs" 
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Official Inbound Email</label>
                        <input 
                            type="email" 
                            name="email" 
                            value={schoolConfig.email || ''} 
                            onChange={handleInputChange} 
                            placeholder="registrar@institution.edu" 
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs" 
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Public Web Domain URL</label>
                        <input 
                            type="url" 
                            name="website" 
                            value={schoolConfig.website || ''} 
                            onChange={handleInputChange} 
                            placeholder="https://www.institution.edu" 
                            className="w-full text-xs text-zinc-800 bg-white border border-zinc-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all outline-hidden shadow-2xs" 
                        />
                    </div>
                </div>
            </div>

            {/* TAB INLINE ACTION BAR */}
            <div className="flex items-center justify-end pt-4 border-t border-zinc-100">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50 rounded-lg shadow-xs transition-all active:scale-98 cursor-pointer gap-2"
                >
                    {isSaving ? "Saving changes..." : "Save General"}
                </button>
            </div>
        </div>
    );
}